import { SigningCosmWasmClient } from "@cosmjs/cosmwasm-stargate";
import { BroadcastMode, StdTx } from "@cosmjs/launchpad";
import { GasPrice } from "@cosmjs/stargate";
import { Keplr, Window as KeplrWindow } from "@keplr-wallet/types";
import {
  isAndroid,
  isMobile,
  saveMobileLinkInfo,
} from "@walletconnect/browser-utils";
import WalletConnect from "@walletconnect/client";
import axios from "axios";
import QRCode from "qrcode.react";

import { Button, Modal, SmallTitle } from "../../../components";
import { supportedChainsMap } from "../../../config";
import { ICosmosConnector } from "../../../interfaces";
import "./KeplrMobile.scss";

const getKeplrWalletConnectV1 = async () => {
  const { KeplrWalletConnectV1 } = await import("@keplr-wallet/wc-client");
  return KeplrWalletConnectV1;
};

declare global {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface Window extends KeplrWindow {}
}

const sendTxWC = async (
  chainId: string,
  tx: StdTx | Uint8Array,
  mode: BroadcastMode,
): Promise<Uint8Array> => {
  const restInstance = axios.create({
    baseURL: supportedChainsMap.cosmos[chainId].rpcUrl,
  });

  const isProtoTx = Buffer.isBuffer(tx) || tx instanceof Uint8Array;

  const params = isProtoTx
    ? {
        tx_bytes: Buffer.from(tx as any).toString("base64"),
        mode: (() => {
          switch (mode) {
            case "async":
              return "BROADCAST_MODE_ASYNC";
            case "block":
              return "BROADCAST_MODE_BLOCK";
            case "sync":
              return "BROADCAST_MODE_SYNC";
            default:
              return "BROADCAST_MODE_UNSPECIFIED";
          }
        })(),
      }
    : {
        tx,
        mode: mode,
      };

  const result = await restInstance.post(
    isProtoTx ? "/cosmos/tx/v1beta1/txs" : "/txs",
    params,
  );

  const txResponse = isProtoTx ? result.data["tx_response"] : result.data;

  if (txResponse.code != null && txResponse.code !== 0) {
    throw new Error(txResponse["raw_log"]);
  }

  return Buffer.from(txResponse.txhash, "hex");
};

class KeplrMobileConnector implements ICosmosConnector {
  cosmJsProviderHooks: ICosmosConnector["cosmJsProviderHooks"];

  createWalletConnect(): WalletConnect {
    const wc = new WalletConnect({
      bridge: "https://bridge.walletconnect.org", // Required
      signingMethods: [
        "keplr_enable_wallet_connect_v1",
        "keplr_sign_amino_wallet_connect_v1",
      ],
      storageId: "walletConnect_keplr",
      qrcodeModal: {
        open: (uri: string, cb: any) => {
          const getNavigateToAppURL = () => {
            if (!uri) {
              return "";
            }

            if (isMobile()) {
              if (isAndroid()) {
                // Save the mobile link.
                saveMobileLinkInfo({
                  name: "Keplr",
                  href: "intent://wcV1#Intent;package=com.chainapsis.keplr;scheme=keplrwallet;end;",
                });

                return `intent://wcV1?${uri}#Intent;package=com.chainapsis.keplr;scheme=keplrwallet;end;`;
              } else {
                // Save the mobile link.
                saveMobileLinkInfo({
                  name: "Keplr",
                  href: "keplrwallet://wcV1",
                });

                return `keplrwallet://wcV1?${uri}`;
              }
            }
            return "";
          };
          const navigateToAppURL = getNavigateToAppURL();
          if (navigateToAppURL) {
            window.location.href = navigateToAppURL;
          }
          this.cosmJsProviderHooks?.setContent(
            <Modal
              id="wc-keplr-mobile-modal"
              onCloseClick={() =>
                this.cosmJsProviderHooks?.setContent(undefined)
              }
              showCloseButton
              className="connect-wallet-modal"
            >
              <div className="connect-wallet-modal__title">
                Mobile{" "}
                <span className="connect-wallet-modal__title--white">
                  Connect
                </span>
              </div>
              {isMobile() ? (
                <>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <Button
                      onClick={() => {
                        if (navigateToAppURL) {
                          window.location.href = navigateToAppURL;
                        }
                      }}
                      variant="PRIMARY"
                      disabled={!navigateToAppURL}
                    >
                      Open App
                    </Button>
                  </div>
                  <div style={{ textAlign: "center", marginTop: "16px" }}>
                    <SmallTitle
                      className="keplr-mobile__text"
                      text="Approve in Keplr Mobile App access to your wallet"
                    ></SmallTitle>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <div style={{ background: "white", padding: "8px" }}>
                      <QRCode size={280} value={uri} />
                    </div>
                  </div>
                  <div style={{ textAlign: "center", marginTop: "8px" }}>
                    <SmallTitle text="Scan this QR code with Keplr Mobile app"></SmallTitle>
                  </div>
                </>
              )}
            </Modal>,
          );
        },
        close: () => this.cosmJsProviderHooks?.setContent(undefined),
      },
    });

    // @ts-ignore
    wc._clientMeta = {
      name: "Gateway",
      description:
        "Gateway Protocol: AI Powered Cross-Chain Liquidity Layer for Tokenized Assets",
      url: "https://mygateway.io",
      icons: ["https://mygateway.io/favicon.png"],
    };

    return wc;
  }
  async activate(chainId: string, silent = false) {
    if (!this.cosmJsProviderHooks) {
      throw new Error("CosmJs Provider missing hooks.");
    }

    const connector = this.createWalletConnect();

    const keplr: Keplr = await new Promise(async (resolve, reject) => {
      const KeplrWalletConnectV1 = await getKeplrWalletConnectV1();

      // Check if connection is already established
      if (!connector.connected) {
        if (silent) {
          throw new Error("Failed to connect Keplr Mobile in silent mode");
        }
        // create new session
        connector.createSession().catch(reject);

        connector.on("connect", (error) => {
          if (error) {
            reject(error);
          } else {
            const keplr = new KeplrWalletConnectV1(connector, {
              sendTx: sendTxWC,
            });
            resolve(keplr);
          }
        });
      } else {
        const keplr = new KeplrWalletConnectV1(connector, {
          sendTx: sendTxWC,
        });
        resolve(keplr);
      }
    });

    await keplr.enable(chainId);

    const info = await keplr.getKey(chainId);
    const offlineSigner = await keplr.getOfflineSignerOnlyAmino(chainId);

    const signingCosmWasmClient = await SigningCosmWasmClient.connectWithSigner(
      supportedChainsMap.cosmos[chainId]?.rpcUrl,
      offlineSigner,
      {
        gasPrice: GasPrice.fromString(
          supportedChainsMap.cosmos[chainId]?.gasPrice,
        ),
      },
    );

    this.cosmJsProviderHooks.setChainId(chainId);
    this.cosmJsProviderHooks.setAddress(info.bech32Address);
    this.cosmJsProviderHooks.setProvider(signingCosmWasmClient);
    this.cosmJsProviderHooks.setMainConnector(this);
  }

  async connectEagerly(chainId: string): Promise<void> {
    return this.activate(chainId, true);
  }

  async deactivate(): Promise<void> {
    if (!this.cosmJsProviderHooks) {
      throw new Error("CosmJs Provider missing hooks.");
    }

    this.cosmJsProviderHooks.setChainId(undefined);
    this.cosmJsProviderHooks.setAddress(undefined);
    this.cosmJsProviderHooks.setProvider(undefined);
  }
}

export const KeplrMobile = new KeplrMobileConnector();
